// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { WidgetSpec } from './dashboardMetrics';
import type { ChatChartSpec, ChatPanoCommand, ChatQueryResult } from '../chatbot/chatbotTypes';

// A make_chart (recharts) chart the user pinned from the chat. Holds the EXACT chart payload so the
// dashboard renders it identically to the chat (same ChatChart component) — never a client recompute,
// so the chat graph and the dashboard graph can't diverge.
export interface PinnedChart {
  id: string;
  chart: ChatChartSpec;
  // When set, this pin is shared to the org for a specific gdb; other org members see it. Local-only
  // pins leave it undefined.
  sharedGdbId?: string;
}

// The center the cinematic Orbit should circle, set from a dashboard chart selection (a plain point,
// since Esri Graphics aren't serializable). The viewer toolbar reads this to orbit the selection.
export interface OrbitCenter {
  x: number;
  y: number;
  z?: number;
  wkid?: number;
}

// Features Khora asked to show on the map (show_on_map). The chatbot (Shell) dispatches this; the
// Project Viewer renders + tours it. `id` is bumped on every dispatch so the viewer re-renders even
// when the same set is requested again. geojson is a WGS84 FeatureCollection.
// One set of already-displayed features to highlight IN PLACE: the viewer resolves `layer` in the
// loaded geodatabase, queries it for the features whose `keyField` is in `keyValues`, and highlights
// those (native highlight + a colored outline at their REAL geometry) — so a 3D pole/wire highlights
// at its true height with no re-drawn geometry. `color` is a name ('green') or #RRGGBB.
export interface HighlightTarget {
  layer: string;
  keyField?: string;
  keyValues?: string[];
  color?: string;
  // Fallback geometry drawn only if the in-place query for this target finds nothing (layer not
  // displayed / key didn't resolve) — so a highlight is never silently lost.
  geojson?: GeoJSON.FeatureCollection;
}

export interface ChatMapHighlight {
  id: number;
  label?: string;
  action?: string; // 'auto' | 'tour' | 'flythrough' | 'none'
  geojson: GeoJSON.FeatureCollection;
  // Preferred highlight path: highlight the already-displayed features in place (correct height, no
  // re-drawn geometry). Each target is one layer + the join keys to select. Empty/absent → the viewer
  // falls back to drawing `geojson` (e.g. a multi-layer set, or when Khora didn't supply a key).
  targets?: HighlightTarget[];
  // Highlight color (a name like 'green' or #RRGGBB) and auto-clear lifetime in ms. A bar click sets
  // both (the bar's color + 5s); show_on_map may set color. Absent → default cyan, and it persists.
  color?: string;
  ttlMs?: number;
}

// A geodatabase feature the user clicked in the Project Viewer, summarized serializably so the
// chatbot can send it to Khora as app_state.selected_feature ("this pole / this feature").
export interface SelectedGdbFeature {
  layer: string;
  attributes: Record<string, string | number>;
}

// A region-of-interest the user drew on the 3D viewer — sent as app_state.selected_region so Khora's
// query_region tool scopes "in this area / here" answers to it. WGS84: bbox is [west, south, east, north]
// and geometry is a GeoJSON polygon (used for a non-rectangular exact filter); area_acres feeds the readout.
export interface SelectedRegion {
  bbox: [number, number, number, number];
  geometry: { type: 'Polygon'; coordinates: number[][][] };
  area_acres: number;
}

// A Khora-initiated region command (trigger_app_action draw_region/clear_region). Bridges the chatbot tree →
// the viewer, which OWNS the sketch — so the viewer can both open a draw and cancel an in-progress one, which
// nulling selectedRegion alone can't do (a mid-draw region is still null). `id` is a nonce so a repeated
// command re-triggers even when identical. 'draw' carries the shape; 'clear' cancels the draw + drops the region.
export type RegionCommand =
  | { kind: 'draw'; mode: 'rectangle' | 'polygon' | 'freehand'; id: number }
  | { kind: 'clear'; id: number };

// Per-feature analysis Khora appended to EXISTING feature popups (add_feature_analysis), keyed by layer
// name. `byKey` maps the join-key value (e.g. a Pole_Tag) to a chart-spec JSON string the popup renders
// as a "Khora Analysis" section. NOT a map layer — it annotates the app's already-displayed features.
export interface FeatureAnalysis {
  keyField: string;
  title?: string;
  byKey: Record<string, string>;
  // Whether the user pinned this analysis to the dashboard. This is the ON/OFF switch for its popup
  // "Khora Analysis" section: Khora computes it OFF (offered via a chat "Add to Dashboard" chip), and
  // only a pinned analysis appears both on the dashboard's "Popup Analyses" list AND in the popups.
  onDashboard: boolean;
}

// Custom widgets + pinned charts + orbit target + chat map highlight + the clicked gdb feature. Lives
// in Redux because the chatbot (Shell) and the dashboard/viewer (Project Viewer) are separate trees.
interface IDashboardState {
  pinnedWidgets: WidgetSpec[];
  pinnedCharts: PinnedChart[];
  orbitCenter: OrbitCenter | null;
  chatHighlight: ChatMapHighlight | null;
  // The last tabular run_sql result (rows the model answered from) → the viewer's bottom rows-panel table.
  chatQueryResult: ChatQueryResult | null;
  // A go_to_pano command from Khora: open the nearest pano to a pole + orient toward it. Consumed by
  // EsriSplitViewer (it holds the pano viewer); the chatbot and viewer trees communicate through here.
  chatPano: ChatPanoCommand | null;
  // Opt-in pano-vision (viewer → chatbot): true while the pano viewer's toggle is on and a pano is open. The
  // chatbot shows the "pano attached" hint and, at send, captures the CURRENT view via the panoImage bridge.
  panoVisionActive: boolean;
  selectedGdbFeature: SelectedGdbFeature | null;
  // A region-of-interest the user drew on the 3D viewer, scoping Khora's "in this area" answers (query_region).
  selectedRegion: SelectedRegion | null;
  // A Khora-initiated region command (draw_region/clear_region), consumed by the viewer.
  regionCommand: RegionCommand | null;
  // Per-feature analysis appended to existing popups (add_feature_analysis), keyed by layer name.
  featureAnalysis: Record<string, FeatureAnalysis>;
  // The _id of the geodatabase currently loaded in the Project Viewer, set by EsriSplitViewer. Keys
  // org-shared dashboard pins to a specific gdb (an order can have multiple gdbs).
  loadedGdbId: string | null;
  // True while the Khora chat drawer is open (mirrored from the Shell). The dashboard panel + launcher
  // slide left by the drawer width so they sit beside Khora instead of hidden under it.
  chatbotOpen: boolean;
}

const initialState: IDashboardState = {
  pinnedWidgets: [],
  pinnedCharts: [],
  orbitCenter: null,
  chatHighlight: null,
  chatQueryResult: null,
  chatPano: null,
  panoVisionActive: false,
  selectedGdbFeature: null,
  selectedRegion: null,
  regionCommand: null,
  featureAnalysis: {},
  loadedGdbId: null,
  chatbotOpen: false,
};

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    pinWidget: (state, action: PayloadAction<WidgetSpec>) => {
      if (!state.pinnedWidgets.some((widget) => widget.id === action.payload.id)) {
        state.pinnedWidgets.push(action.payload);
      }
    },
    setWidgetShared: (state, action: PayloadAction<{ id: string; sharedGdbId?: string }>) => {
      const widget = state.pinnedWidgets.find((entry) => entry.id === action.payload.id);
      if (widget) widget.sharedGdbId = action.payload.sharedGdbId;
    },
    unpinWidget: (state, action: PayloadAction<string>) => {
      state.pinnedWidgets = state.pinnedWidgets.filter((widget) => widget.id !== action.payload);
    },
    pinChart: (state, action: PayloadAction<PinnedChart>) => {
      // Upsert: re-pinning the same id REPLACES the snapshot in place — this is how a slider chart's
      // "Update pinned chart to current value" pushes a new value without an unpin/pin dance.
      const existing = state.pinnedCharts.findIndex((chart) => chart.id === action.payload.id);
      if (existing >= 0) state.pinnedCharts[existing] = action.payload;
      else state.pinnedCharts.push(action.payload);
    },
    unpinChart: (state, action: PayloadAction<string>) => {
      state.pinnedCharts = state.pinnedCharts.filter((chart) => chart.id !== action.payload);
    },
    setChartShared: (state, action: PayloadAction<{ id: string; sharedGdbId?: string }>) => {
      const pinned = state.pinnedCharts.find((chart) => chart.id === action.payload.id);
      if (pinned) pinned.sharedGdbId = action.payload.sharedGdbId;
    },
    setOrbitCenter: (state, action: PayloadAction<OrbitCenter | null>) => {
      state.orbitCenter = action.payload;
    },
    setChatHighlight: (state, action: PayloadAction<ChatMapHighlight | null>) => {
      state.chatHighlight = action.payload;
    },
    setChatQueryResult: (state, action: PayloadAction<ChatQueryResult | null>) => {
      state.chatQueryResult = action.payload;
    },
    setChatPano: (state, action: PayloadAction<ChatPanoCommand | null>) => {
      state.chatPano = action.payload;
    },
    setPanoVisionActive: (state, action: PayloadAction<boolean>) => {
      state.panoVisionActive = action.payload;
    },
    setSelectedGdbFeature: (state, action: PayloadAction<SelectedGdbFeature | null>) => {
      state.selectedGdbFeature = action.payload;
    },
    setSelectedRegion: (state, action: PayloadAction<SelectedRegion | null>) => {
      state.selectedRegion = action.payload;
    },
    setRegionCommand: (state, action: PayloadAction<RegionCommand | null>) => {
      state.regionCommand = action.payload;
    },
    setLoadedGdbId: (state, action: PayloadAction<string | null>) => {
      state.loadedGdbId = action.payload;
    },
    setChatbotOpen: (state, action: PayloadAction<boolean>) => {
      state.chatbotOpen = action.payload;
    },
    setFeatureAnalysis: (state, action: PayloadAction<Record<string, FeatureAnalysis>>) => {
      // Merge by layer (last-wins per layer) so analyses added across turns accumulate. Preserve an
      // existing pin: if the user already added this layer's analysis to the dashboard, a fresh Khora
      // recompute must not silently un-pin it (a restore from a saved session keeps its saved flag).
      Object.entries(action.payload).forEach(([layer, analysis]) => {
        const wasPinned = state.featureAnalysis[layer]?.onDashboard;
        state.featureAnalysis[layer] = { ...analysis, onDashboard: wasPinned ?? analysis.onDashboard ?? false };
      });
    },
    // Toggle whether a layer's per-feature analysis is pinned to the dashboard (and thus shown in popups).
    setFeatureAnalysisOnDashboard: (state, action: PayloadAction<{ layer: string; onDashboard: boolean }>) => {
      const entry = state.featureAnalysis[action.payload.layer];
      if (entry) entry.onDashboard = action.payload.onDashboard;
    },
    // Remove a layer's per-feature analysis entirely — drops it from the dashboard AND the popups.
    removeFeatureAnalysis: (state, action: PayloadAction<string>) => {
      delete state.featureAnalysis[action.payload];
    },
    // Wipe an order's Khora dashboard artifacts (pins + highlight + per-feature analysis + the clicked
    // feature + orbit target) so nothing bleeds into the next order/gdb. Dispatched on order/session
    // switch, BEFORE that order's saved session restores its own. loadedGdbId is left alone (viewer-owned).
    resetDashboardArtifacts: (state) => {
      state.pinnedWidgets = [];
      state.pinnedCharts = [];
      state.orbitCenter = null;
      state.chatHighlight = null;
      state.chatQueryResult = null;
      state.selectedGdbFeature = null;
      state.selectedRegion = null;
      state.regionCommand = null;
      // A pending go_to_pano (and the pano-vision flag) must clear too, else a stale command re-fires on the
      // next order/gdb once its pano layer builds.
      state.chatPano = null;
      state.panoVisionActive = false;
      state.featureAnalysis = {};
    },
  },
});

export const {
  pinWidget, unpinWidget, setWidgetShared, pinChart, unpinChart, setChartShared,
  setOrbitCenter, setChatHighlight, setChatQueryResult, setChatPano, setPanoVisionActive, setSelectedGdbFeature, setSelectedRegion, setRegionCommand, setLoadedGdbId, setFeatureAnalysis,
  setFeatureAnalysisOnDashboard, removeFeatureAnalysis, resetDashboardArtifacts, setChatbotOpen,
} = dashboardSlice.actions;
export const { reducer } = dashboardSlice;
export default reducer;
